import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  const [siteClient, setSiteClient] = useState(null); // Resolved client for custom-domain customer site

  // Resolve client from current domain at app boot.
  // If the request comes through a tenant's custom domain (e.g. repairshop-detroit.com)
  // the backend returns that client's id/name; otherwise client_id is null (main site → corporate).
  useEffect(() => {
    const host = window.location.host;
    axios.get(`${API_URL}/api/clients/by-domain`, { params: { host } })
      .then(({ data }) => { if (data && data.client_id) setSiteClient(data); })
      .catch(() => {});
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      // Try to refresh token
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Global 401 auto-refresh: the access-token cookie lasts 8h, but staff keep
  // the POS open all day. Without this, the first request after expiry (e.g.
  // creating a ticket) 401s and bounces them to the login page. Here we
  // transparently refresh once (refresh token lasts 7 days) and retry the
  // original request; only if the refresh itself fails do we log out.
  useEffect(() => {
    let refreshing = null;
    const id = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const original = error.config;
        const status = error.response?.status;
        const url = original?.url || '';
        const isAuthCall = url.includes('/api/auth/');
        if (status === 401 && original && !original._retried && !isAuthCall) {
          original._retried = true;
          try {
            if (!refreshing) {
              refreshing = axios
                .post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
                .finally(() => { refreshing = null; });
            }
            await refreshing;
            return axios(original);
          } catch (e) {
            setUser(false);
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Fetch full user data (with location info) from /me
      try {
        const { data: meData } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(meData);
        return { success: true, user: meData };
      } catch {
        setUser(data);
        return { success: true, user: data };
      }
    } catch (e) {
      const errorMessage = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { success: false, error: errorMessage };
    }
  };

  const register = async (email, password, name, phone) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name, phone },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      const errorMessage = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Ignore errors
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, siteClient }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
